import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { useState, useEffect, useRef } from 'react';
import { alpha, theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useCheckItem, useCheckRun, useSaveCheckItem, useUploadPhoto, useDeletePhoto } from '../lib/queries.js';
import { SyncBanner } from '../components/SyncBanner.js';
import { PhotoMarkup } from '../components/PhotoMarkup.js';
import { removePendingPhoto } from '../lib/offline/outbox.js';
import { NOT_ON_PHONE } from '../lib/offline/messages.js';
import type { CheckItemDetail } from '../lib/queries.js';
import { areaOf, itemPath } from '../lib/areas.js';
import { actorLabel } from '../lib/people.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { EquipmentHistory } from '../components/EquipmentHistory.js';
import { ResolveIssue } from '../components/ResolveIssue.js';
import type { CheckStatus } from '@keetcheck/shared';

const STATUSES: { key: CheckStatus; label: string; tone: keyof typeof theme.status }[] = [
  { key: 'ok',        label: 'OK',        tone: 'ok'      },
  { key: 'missing',   label: 'Missing',   tone: 'danger'  },
  { key: 'broken',    label: 'Broken',    tone: 'danger'  },
  { key: 'attention', label: 'Attention', tone: 'warn'    },
  { key: 'rectified', label: 'Rectified', tone: 'service' },
];

/** Keyed by item so each wizard step starts with fresh form state. */
export function CheckItemPage() {
  const { itemId } = useParams<{ itemId: string }>();
  return <CheckItemStep key={itemId} />;
}

function CheckItemStep() {
  const { runId, itemId } = useParams<{ runId: string; itemId: string }>();
  const nav = useNavigate();
  const back = useBack(`/checks/${runId}`);
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  // The run's list drives the wizard: position, previous/next, what's left.
  // Started from an area ("Drivers side"), it stays inside that area.
  const [params] = useSearchParams();
  const area = params.get('area');
  const { data: run } = useCheckRun(orgId, runId ?? '');
  const allItems = run?.items ?? [];
  const fromList = allItems.find((i) => i.id === itemId);
  const runItems = area && fromList && areaOf(fromList) === area
    ? allItems.filter((i) => areaOf(i) === area)
    : allItems;
  const index = runItems.findIndex((i) => i.id === itemId);
  const [finished, setFinished] = useState(false);
  const { data: item, isLoading } = useCheckItem(
    itemId ?? '',
    fromList && ({ ...fromList, photos: [] } as CheckItemDetail),
  );
  const isDraft = run ? run.status === 'draft' : true;
  const [needReading, setNeedReading] = useState(false);
  const saveMut = useSaveCheckItem(orgId, runId ?? '');
  const uploadMut = useUploadPhoto(orgId, runId ?? '');
  const deleteMut = useDeletePhoto(orgId, runId ?? '', itemId ?? '');

  // local state mirrors server, allows immediate interaction
  const [status, setStatus] = useState<CheckStatus | null>(null);
  const [note, setNote] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [reading, setReading] = useState('');
  const [dirty, setDirty] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (item && !initialized.current) {
      initialized.current = true;
      setStatus(item.status);
      setNote(item.note ?? '');
      setExternalRef(item.externalRef ?? '');
      setFlagged(item.flagged);
      setReading(item.reading ?? '');
    }
  }, [item]);

  // Tasks and readings aren't things that go missing or break.
  const kind = item?.equipmentKind ?? 'item';
  const statuses = kind === 'item' ? STATUSES
    : STATUSES.filter((s) => (kind === 'task' ? ['ok', 'attention', 'rectified'] : ['ok', 'attention']).includes(s.key));

  function goTo(id: string | undefined, inArea = area) {
    if (id) nav(itemPath(runId ?? '', id, inArea), { replace: true });
    else setFinished(true); // nothing left here — show the done screen
  }

  /** Next item still to check, after this one first, then from the top. */
  function nextUnchecked(): string | undefined {
    const open = (i: (typeof runItems)[number]) => i.id !== itemId && i.status == null;
    return (runItems.slice(index + 1).find(open) ?? runItems.slice(0, Math.max(index, 0)).find(open))?.id;
  }

  /** Everything typed on this step, so moving on never loses it. */
  function pending() {
    return dirty
      ? { note, externalRef, flagged, ...(kind === 'reading' ? { reading: reading.trim() } : {}) }
      : {};
  }

  function handleStatus(s: CheckStatus) {
    // OK is the common case: save and move straight on. Issues stay for a note or photo.
    if (s === 'ok' && kind === 'reading' && !reading.trim()) { setNeedReading(true); return; }
    setStatus(s);
    saveMut.mutate({ itemId: itemId!, status: s, ...pending() });
    setDirty(false);
    if (s === 'ok') goTo(nextUnchecked());
  }

  function handleSave() {
    saveMut.mutate({ itemId: itemId!, ...pending() });
    setDirty(false);
  }

  function handleNext() {
    if (dirty) handleSave();
    goTo(nextUnchecked() ?? runItems[index + 1]?.id);
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  // A new photo opens the markup editor first (circle the problem, add a label).
  const [markup, setMarkup] = useState<File | null>(null);
  const [viewing, setViewing] = useState<{ url: string; originalUrl?: string | null } | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setMarkup(file);
    e.target.value = '';
  }

  if (finished) {
    const elsewhere = allItems.find((i) => i.status == null && i.id !== itemId);
    const nextArea = elsewhere && areaOf(elsewhere);
    return (
      <PageShell noNav>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, padding: 24, background: theme.bg }}>
          <div style={{ textAlign: 'center', color: theme.status.ok.fg }}>
            <Icon name="check" size={40} />
          </div>
          <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 800, color: theme.text }}>
            {area ? `${area} done` : 'Everything checked'}
          </div>
          <div style={{ textAlign: 'center', fontSize: 14, color: theme.textMuted, marginBottom: 8 }}>
            {elsewhere
              ? `${allItems.filter((i) => i.status == null && i.id !== itemId).length} items left on the rest of the check.`
              : 'Every item on this check has a status.'}
          </div>
          {elsewhere
            ? <button onClick={() => goTo(elsewhere.id, area ? nextArea : null)} style={bigBtn}>
                {area ? `Next: ${nextArea}` : 'Next unchecked item'}
              </button>
            : <button onClick={() => nav(`/checks/${runId}/submit`, { replace: true })} style={bigBtn}>
                Review and submit
              </button>}
          <button onClick={back} style={{ ...bigBtn, background: theme.surface, color: theme.textDim, border: `1px solid ${theme.border}` }}>
            Back to checklist
          </button>
        </div>
      </PageShell>
    );
  }

  if (isLoading) return <PageShell noNav><Msg onBack={back}>{navigator.onLine ? 'Loading…' : NOT_ON_PHONE}</Msg></PageShell>;
  if (!item) return <PageShell noNav><Msg onBack={back}>Not found</Msg></PageShell>;

  const photos = item.photos ?? [];
  const markupLayer = markup && (
    <PhotoMarkup file={markup}
      onDone={(blob) => { uploadMut.mutate({ itemId: itemId!, file: new File([blob], 'marked.jpg', { type: 'image/jpeg' }), original: markup }); setMarkup(null); }}
      onSkip={() => { uploadMut.mutate({ itemId: itemId!, file: markup }); setMarkup(null); }}
      onCancel={() => setMarkup(null)} />
  );
  const viewer = viewing && (
    <div role="dialog" aria-label="Photo" onClick={() => setViewing(null)} style={{
      position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16,
    }}>
      <img src={showOriginal && viewing.originalUrl ? viewing.originalUrl : viewing.url} alt="" style={{ maxWidth: '100%', maxHeight: '80dvh', borderRadius: 8 }} />
      {viewing.originalUrl && (
        <button onClick={(e) => { e.stopPropagation(); setShowOriginal(!showOriginal); }} style={{
          padding: '8px 14px', borderRadius: 8, border: 'none', background: '#fff', color: '#111', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>{showOriginal ? 'Show marked up' : 'Show original'}</button>
      )}
    </div>
  );
  const pendingPhotos = item.pendingPhotos ?? [];
  const prevId = index > 0 ? runItems[index - 1]?.id : undefined;
  const nextId = index >= 0 && index < runItems.length - 1 ? runItems[index + 1]?.id : undefined;
  const left = runItems.filter((i) => i.status == null && i.id !== itemId).length;

  return (
    <PageShell noNav>
      {markupLayer}
      {viewer}
      {/* sticky header */}
      <div style={{
        padding: '12px 16px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={back} style={iconBtn} aria-label="Back">
            <Icon name="back" size={18} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 17, fontWeight: 700, color: theme.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {item.equipmentName ?? 'Item'}
            </div>
            {/* these two lines are always one line each, so the buttons below don't move between items */}
            <div style={{ fontSize: 12, color: theme.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[item.serialNumber && `#${item.serialNumber}`, item.sectionName].filter(Boolean).join(' · ') || ' '}
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.checkedAt
                ? <>Checked by {actorLabel(item.checkedByName, item.checkedByActive)} ·{' '}
                    {new Date(item.checkedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</>
                : 'Not checked yet'}
            </div>
          </div>
          {item.flagged && (
            <Icon name="flag" size={18} style={{ color: theme.status.danger.dot, flexShrink: 0 }} />
          )}
        </div>
        {index >= 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <button onClick={() => goTo(prevId)} disabled={!prevId} style={{ ...stepBtn, opacity: prevId ? 1 : 0.4 }} aria-label="Previous item">
              <Icon name="chevronL" size={16} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ height: 4, background: theme.surface2, borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${((index + 1) / runItems.length) * 100}%`, height: '100%', background: theme.accent }} />
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 3, textAlign: 'center' }}>
                {area && runItems !== allItems ? `${area} · ` : ''}{index + 1} of {runItems.length} · {left === 0 ? 'rest checked' : `${left} left`}
              </div>
            </div>
            <button onClick={() => goTo(nextId)} disabled={!nextId} style={{ ...stepBtn, opacity: nextId ? 1 : 0.4 }} aria-label="Next item">
              <Icon name="chevron" size={16} />
            </button>
          </div>
        )}
      </div>

      <SyncBanner runId={runId ?? ''} />

      {/* scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg }}>

        {/* submitted check: an issue can be marked fixed */}
        {!isDraft && <ResolveIssue orgId={orgId} item={item} />}

        {/* carry-forward banner */}
        {item.carryForward && item.previousStatus && (
          <div style={{
            margin: '12px 16px 0',
            padding: '10px 14px',
            background: theme.status.warn.bg,
            border: `1px solid ${alpha(theme.status.warn.dot, 13)}`,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon name="warning" size={16} style={{ color: theme.status.warn.fg, flexShrink: 0 }} />
            <div style={{ fontSize: 13, color: theme.status.warn.fg, fontWeight: 500 }}>
              Carried forward from previous check —{' '}
              <strong>{item.previousStatus}</strong>
            </div>
          </div>
        )}

        {/* what to check — always present and a fixed height, so OK stays put */}
        <div style={{
          margin: '16px 16px 0', fontSize: 15, color: theme.text, lineHeight: 1.35,
          height: 42, overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {checkPrompt(item.equipmentName ?? 'this item', kind, item.expected)}
        </div>

        {/* reading value (odometer, fuel level…) */}
        {kind === 'reading' && (
          <div style={{ padding: '16px 16px 0' }}>
            <Label>Reading</Label>
            <input
              value={reading}
              onChange={(e) => { setReading(e.target.value); setDirty(true); setNeedReading(false); }}
              autoFocus={!item.reading}
              placeholder={/odometer/i.test(item.equipmentName ?? '') ? 'e.g. 84512 km' : 'e.g. 1/2 full'}
              inputMode={/odometer/i.test(item.equipmentName ?? '') ? 'numeric' : 'text'}
              style={{
                width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                background: theme.surface, border: `1px solid ${needReading ? theme.status.danger.dot : theme.border}`, borderRadius: 10,
                fontSize: 16, color: theme.text, fontFamily: theme.font, outline: 'none',
              }}
            />
          </div>
        )}

        {/* status picker: one big OK (saves and moves on), issues underneath */}
        <div style={{ padding: '16px 16px 0' }}>
          {needReading && (
            <div style={{ fontSize: 13, color: theme.status.danger.fg, marginBottom: 8 }}>
              Enter the reading first.
            </div>
          )}
          {isDraft && (
            <button
              onClick={() => handleStatus('ok')}
              disabled={saveMut.isPending && status === 'ok'}
              style={{
                width: '100%', padding: '16px 0', marginBottom: 10, borderRadius: 12,
                border: `2px solid ${theme.status.ok.dot}`,
                background: status === 'ok' ? theme.status.ok.dot : theme.status.ok.bg,
                color: status === 'ok' ? '#fff' : theme.status.ok.fg,
                fontSize: 17, fontWeight: 800, cursor: 'pointer', fontFamily: theme.font,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Icon name="check" size={20} /> OK{left > 0 ? ' — next' : ''}
            </button>
          )}
          <Label>Something wrong?</Label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${statuses.length - 1}, 1fr)`,
            gap: 8,
          }}>
            {statuses.filter((s) => s.key !== 'ok').map((s) => {
              const isActive = status === s.key;
              const tone = theme.status[s.tone];
              return (
                <button
                  key={s.key}
                  disabled={!isDraft}
                  onClick={() => handleStatus(s.key)}
                  style={{
                    padding: '10px 4px',
                    borderRadius: 10,
                    border: isActive ? `2px solid ${tone.dot}` : `1.5px solid ${theme.border}`,
                    background: isActive ? tone.bg : theme.surface,
                    color: isActive ? tone.fg : theme.textDim,
                    fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', lineHeight: 1.3,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                    fontFamily: theme.font,
                    transition: 'border-color .1s, background .1s',
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: 4,
                    background: isActive ? tone.dot : theme.borderHi,
                  }} />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* note */}
        <div style={{ padding: '16px 16px 0' }}>
          <Label>Note</Label>
          <textarea
            value={note}
            onChange={(e) => { setNote(e.target.value); setDirty(true); }}
            placeholder="Add a note…"
            rows={3}
            style={{
              width: '100%', padding: '10px 12px',
              background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 10, resize: 'none', fontSize: 14,
              color: theme.text, fontFamily: theme.font,
              lineHeight: 1.5, boxSizing: 'border-box',
              outline: 'none',
            }}
          />
        </div>

        {/* external ref */}
        <div style={{ padding: '12px 16px 0' }}>
          <Label>External reference</Label>
          <input
            value={externalRef}
            onChange={(e) => { setExternalRef(e.target.value); setDirty(true); }}
            placeholder="e.g. job ticket #1234"
            style={{
              width: '100%', padding: '10px 12px',
              background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 10, fontSize: 14,
              color: theme.text, fontFamily: theme.font,
              boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>

        {/* flag toggle */}
        <div style={{ padding: '12px 16px 0' }}>
          <button
            onClick={() => { setFlagged(!flagged); setDirty(true); }}
            aria-pressed={flagged}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', width: '100%',
              background: flagged ? theme.status.danger.bg : theme.surface,
              border: `1px solid ${flagged ? alpha(theme.status.danger.dot, 27) : theme.border}`,
              borderRadius: 10, cursor: 'pointer',
              fontFamily: theme.font,
            }}
          >
            <Icon
              name="flag"
              size={17}
              style={{ color: flagged ? theme.status.danger.dot : theme.textMuted }}
            />
            <span style={{
              fontSize: 14, fontWeight: 600,
              color: flagged ? theme.status.danger.fg : theme.textDim,
            }}>
              {flagged ? 'Flagged for follow-up' : 'Flag for follow-up'}
            </span>
          </button>
        </div>

        {/* photos */}
        <div style={{ padding: '16px 16px 0' }}>
          <Label>Photos</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative' }}>
                <img
                  src={p.url}
                  alt=""
                  onClick={() => { setShowOriginal(false); setViewing({ url: p.url, originalUrl: p.originalUrl ?? null }); }}
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: `1px solid ${theme.border}`, cursor: 'zoom-in' }}
                />
                <button aria-label="Remove photo"
                  onClick={() => deleteMut.mutate(p.id)}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 20, height: 20, borderRadius: 10,
                    background: theme.status.danger.dot, border: 'none',
                    color: '#fff', fontSize: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            {/* taken here, still waiting to upload */}
            {pendingPhotos.map((p) => (
              <div key={p.id} style={{ position: 'relative' }}>
                <img src={p.url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: `1px dashed ${theme.borderHi}`, opacity: 0.75 }} />
                <span style={{
                  position: 'absolute', left: 4, bottom: 4, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6,
                  background: theme.status.warn.bg, color: theme.status.warn.fg,
                }}>Waiting</span>
                <button aria-label="Remove photo" onClick={() => removePendingPhoto(p.id)} style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10,
                  background: theme.status.danger.dot, border: 'none', color: '#fff', fontSize: 12, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>×</button>
              </div>
            ))}

            {/* add photo tile */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMut.isPending}
              style={{
                width: 72, height: 72, borderRadius: 8,
                border: `1.5px dashed ${theme.borderHi}`,
                background: theme.surface,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 4, cursor: 'pointer', color: theme.textMuted,
              }}
            >
              {uploadMut.isPending
                ? <span style={{ fontSize: 10 }}>…</span>
                : <><Icon name="camera" size={20} /><span style={{ fontSize: 9 }}>Add</span></>}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* save / cancel (when not moving on yet) */}
        {dirty && isDraft && (
          <div style={{ padding: '16px 16px 32px', display: 'flex', gap: 10 }}>
            <button
              onClick={() => {
                initialized.current = false;
                setDirty(false);
              }}
              style={{
                flex: 1, padding: '12px 0',
                background: theme.surface, border: `1px solid ${theme.border}`,
                borderRadius: 12, fontSize: 14, fontWeight: 600,
                color: theme.textDim, cursor: 'pointer', fontFamily: theme.font,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveMut.isPending}
              style={{
                flex: 2, padding: '12px 0',
                background: theme.accent, border: 'none',
                borderRadius: 12, fontSize: 14, fontWeight: 700,
                color: theme.accentInk, cursor: 'pointer', fontFamily: theme.font,
              }}
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {/* an issue was recorded: add details above, then move on */}
        {isDraft && status && status !== 'ok' && (
          <div style={{ padding: '16px 16px 0' }}>
            <button onClick={handleNext} style={{
              width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
              background: theme.accent, color: theme.accentInk, fontSize: 15, fontWeight: 700,
              cursor: 'pointer', fontFamily: theme.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              {dirty ? 'Save and next' : 'Next'} <Icon name="chevron" size={16} />
            </button>
          </div>
        )}

        {/* this item's results on earlier checks */}
        <div style={{ padding: dirty ? '0 16px 32px' : '20px 16px 32px' }}>
          <EquipmentHistory orgId={orgId} equipmentId={item.equipmentId} excludeItemId={item.id} />
        </div>
      </div>
    </PageShell>
  );
}

/** "Expected: 3", "Target: 1/2 full", or a plain question when the sheet gives nothing. */
function checkPrompt(name: string, kind: string, expected: string | null | undefined): React.ReactNode {
  const e = expected?.trim();
  const has = !!e && !/^check$/i.test(e);
  if (kind === 'reading') {
    return has
      ? <><span style={{ color: theme.textMuted }}>Target: </span><b>{e}</b></>
      : <>Record the reading for <b>{name}</b>.</>;
  }
  if (kind === 'task') return has ? <b>{e}</b> : <>Is <b>{name}</b> done?</>;
  return has
    ? <><span style={{ color: theme.textMuted }}>Expected: </span><b>{e}</b> — present and OK?</>
    : <>Is <b>{name}</b> present and OK?</>;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: theme.textDim, marginBottom: 8, letterSpacing: 0.2 }}>
      {children}
    </div>
  );
}

/** Loading / not found. The page hides the bottom nav, so keep a way out. */
function Msg({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
      {children}
      <div style={{ marginTop: 16 }}>
        <button onClick={onBack} style={{
          background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10,
          padding: '8px 16px', color: theme.textDim, fontFamily: theme.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>Go back</button>
      </div>
    </div>
  );
}

const bigBtn: React.CSSProperties = {
  width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
  background: theme.accent, color: theme.accentInk, fontSize: 15, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
};

const stepBtn: React.CSSProperties = {
  width: 40, height: 32, border: `1px solid ${theme.border}`, flexShrink: 0,
  background: theme.surface2, color: theme.text, borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
